import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    difficulty: 'easy',
    youtube_link: '',
    pdf_link: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [uploadingUnit, setUploadingUnit] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [unitNotesStatus, setUnitNotesStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    }
    fetchQuestions();
    fetchUnitNotesStatus();
  }, [navigate]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get('https://codecruxdsa.onrender.com/api/questions');
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchUnitNotesStatus = async () => {
    try {
      const response = await axios.get('https://codecruxdsa.onrender.com/api/notes/all');
      const statusMap = {};
      response.data.forEach(note => {
        statusMap[note.unit_number] = {
          filename: note.filename,
          uploadedAt: note.uploaded_at
        };
      });
      setUnitNotesStatus(statusMap);
    } catch (error) {
      console.error('Error fetching unit notes status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    
    try {
      if (editingId) {
        await axios.put(`https://codecruxdsa.onrender.com/api/questions/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Question updated successfully!');
      } else {
        await axios.post('https://codecruxdsa.onrender.com/api/questions', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Question added successfully!');
      }
      
      setFormData({ title: '', topic: '', difficulty: 'easy', youtube_link: '', pdf_link: '' });
      setEditingId(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Error saving question. Please try again.');
    }
  };

  const handleEdit = (question) => {
    setFormData({
      title: question.title,
      topic: question.topic,
      difficulty: question.difficulty,
      youtube_link: question.youtube_link || '',
      pdf_link: question.pdf_link || ''
    });
    setEditingId(question.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`https://codecruxdsa.onrender.com/api/questions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Question deleted successfully!');
        fetchQuestions();
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Error deleting question. Please try again.');
      }
    }
  };

  const handleFileUpload = async (e, unit) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file only');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('unit', unit);

    const token = localStorage.getItem('adminToken');
    setUploadingUnit(unit);
    setUploadProgress({ ...uploadProgress, [unit]: 0 });

    console.log('=== FRONTEND UPLOAD DEBUG ===');
    console.log('Unit:', unit);
    console.log('File:', file.name, file.type, file.size);
    console.log('FormData entries:');
    for (let pair of uploadFormData.entries()) {
      console.log(pair[0], pair[1]);
    }
    console.log('============================');

    try {
      const response = await axios.post(
        'https://codecruxdsa.onrender.com/api/upload/notes',
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`
            // ✅ NO Content-Type header - axios sets it automatically with boundary
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [unit]: percentCompleted }));
          }
        }
      );

      console.log('✅ Upload successful:', response.data);
      alert(`✅ Unit ${unit} notes uploaded successfully to Cloudinary!`);
      
      fetchUnitNotesStatus();
      e.target.value = '';
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      
      if (error.response) {
        console.error('Server error response:');
        console.error('Status:', error.response.status);
        console.error('Data:', error.response.data);
        
        alert(`❌ Upload failed: ${error.response.data.error || 'Unknown error'}`);
      } else if (error.request) {
        console.error('No response received:', error.request);
        alert('❌ No response from server. Check your connection.');
      } else {
        console.error('Error setting up request:', error.message);
        alert(`❌ Upload error: ${error.message}`);
      }
    } finally {
      setUploadingUnit(null);
      setUploadProgress(prev => ({ ...prev, [unit]: 0 }));
    }
  };

  const handleDeleteNote = async (unit) => {
    if (window.confirm(`Are you sure you want to delete Unit ${unit} notes from Cloudinary?`)) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`https://codecruxdsa.onrender.com/api/notes/unit${unit}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert(`Unit ${unit} notes deleted successfully!`);
        fetchUnitNotesStatus();
      } catch (error) {
        console.error('Error deleting note:', error);
        alert('Error deleting note. Please try again.');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          Manage Questions
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Upload Notes (Cloudinary)
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'questions' && (
          <div className="questions-management">
            <div className="form-section">
              <h2>{editingId ? 'Edit Question' : 'Add New Question'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Question Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      placeholder="Enter question title"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Topic *</label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => setFormData({...formData, topic: e.target.value})}
                      required
                      placeholder="e.g., Arrays, Linked Lists"
                    />
                  </div>

                  <div className="form-group">
                    <label>Difficulty *</label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>YouTube Link</label>
                    <input
                      type="url"
                      value={formData.youtube_link}
                      onChange={(e) => setFormData({...formData, youtube_link: e.target.value})}
                      placeholder="https://youtube.com/..."
                    />
                  </div>

                  <div className="form-group">
                    <label>PDF Link</label>
                    <input
                      type="url"
                      value={formData.pdf_link}
                      onChange={(e) => setFormData({...formData, pdf_link: e.target.value})}
                      placeholder="PDF URL or upload path"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    {editingId ? 'Update Question' : 'Add Question'}
                  </button>
                  {editingId && (
                    <button 
                      type="button" 
                      onClick={() => {
                        setFormData({ title: '', topic: '', difficulty: 'easy', youtube_link: '', pdf_link: '' });
                        setEditingId(null);
                      }}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="questions-list-admin">
              <h2>All Questions ({questions.length})</h2>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Topic</th>
                      <th>Difficulty</th>
                      <th>Resources</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((question) => (
                      <tr key={question.id}>
                        <td>{question.title}</td>
                        <td>{question.topic}</td>
                        <td>
                          <span className={`badge ${question.difficulty}`}>
                            {question.difficulty}
                          </span>
                        </td>
                        <td>
                          {question.youtube_link && <span className="resource-badge">📹</span>}
                          {question.pdf_link && <span className="resource-badge">📄</span>}
                        </td>
                        <td>
                          <button onClick={() => handleEdit(question)} className="edit-btn">Edit</button>
                          <button onClick={() => handleDelete(question.id)} className="delete-btn">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="notes-upload">
            <h2>Upload Unit Notes to Cloudinary</h2>
            <p className="upload-info">
              📤 Upload PDF files for each unit (Unit 1-5) <br />
              ☁️ Files will be stored in Cloudinary cloud storage <br />
              📦 Maximum file size: 10MB per PDF
            </p>
            
            <div className="upload-grid">
              {[1, 2, 3, 4, 5].map((unit) => (
                <div key={unit} className="upload-card">
                  <h3>Unit {unit}</h3>
                  
                  {unitNotesStatus[unit] && (
                    <div className="existing-file-info">
                      <p className="file-name">
                        📄 {unitNotesStatus[unit].filename}
                      </p>
                      <p className="upload-date">
                        Uploaded: {new Date(unitNotesStatus[unit].uploadedAt).toLocaleDateString()}
                      </p>
                      <button 
                        onClick={() => handleDeleteNote(unit)}
                        className="delete-note-btn"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}

                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, unit)}
                    id={`unit-${unit}`}
                    style={{ display: 'none' }}
                    disabled={uploadingUnit !== null}
                  />
                  
                  <label 
                    htmlFor={`unit-${unit}`} 
                    className={`upload-btn ${uploadingUnit === unit ? 'uploading' : ''}`}
                  >
                    {uploadingUnit === unit ? (
                      <>
                        <span className="spinner">⏳</span>
                        Uploading {uploadProgress[unit]}%
                      </>
                    ) : unitNotesStatus[unit] ? (
                      '🔄 Replace PDF'
                    ) : (
                      '📤 Choose PDF'
                    )}
                  </label>

                  {uploadingUnit === unit && (
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress[unit]}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="upload-instructions">
              <h3>📋 Upload Instructions:</h3>
              <ul>
                <li>✅ Only PDF files are allowed</li>
                <li>✅ Maximum file size is 10MB</li>
                <li>✅ Files are stored securely in Cloudinary</li>
                <li>✅ Replace existing files by uploading new ones</li>
                <li>✅ Users can download files directly from cloud</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
